import React, { useState, useEffect } from 'react';
import TacticalMap from './TacticalMap';
import { LatLng, Contact, Alert, NewsItem } from '../types';
import { Menu, Radio, Crosshair } from 'lucide-react';
import TacticalHUD from './TacticalHUD';

interface CommandCenterProps {
    userLocation: LatLng | null;
    userProfile: Contact | null;
    contacts: Contact[];
    hazards: Alert[];
    globalNews: NewsItem[];
    onSos: () => void;
    onOpenMenu: () => void;
    onRegionChange: (center: LatLng) => void;
    onSelectHazard: (h: Alert) => void;
    onSelectNews: (n: NewsItem) => void;
    onOpenChat: (contactId: string) => void;
    onViewProfile: (contactId: string) => void;
    onSendPulse: (contactId: string) => void;
    onNavigate: (page: string) => void;
    initialFocus?: any;
}

const CommandCenter: React.FC<CommandCenterProps> = ({
    userLocation,
    userProfile,
    contacts,
    hazards,
    globalNews,
    onSos,
    onOpenMenu,
    onRegionChange,
    onSelectHazard,
    onSelectNews,
    onOpenChat,
    onViewProfile,
    onSendPulse,
    onNavigate,
    initialFocus
}) => {
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
    const [flyToLocation, setFlyToLocation] = useState<LatLng | null>(null);
    const [mapStyle, setMapStyle] = useState<'SATELLITE' | 'DARK' | 'STREET'>('SATELLITE');

    useEffect(() => {
        if (initialFocus) {
            if (initialFocus.lat && initialFocus.lng) {
                setFlyToLocation({ lat: initialFocus.lat, lng: initialFocus.lng });
            }
        }
    }, [initialFocus]);

    const handleSelectContact = (id: string) => {
        setSelectedContactId(id);
        const contact = contacts.find(c => c.id === id);
        if (contact) {
            setFlyToLocation(contact.coordinates);
        }
    };

    const handleHudAction = (action: string, payload?: any) => {
        if (!selectedContactId) return;

        switch (action) {
            case 'CHAT':
                onOpenChat(selectedContactId);
                break;
            case 'PROFILE':
                onViewProfile(selectedContactId);
                break;
            case 'PULSE':
                onSendPulse(selectedContactId);
                break;
            case 'TRACK':
                // Already tracking if selected
                break;
            case 'LOVE':
                // Implement love reaction if needed
                break;
            case 'WARN':
                // Implement warn reaction if needed
                break;
        }
    };

    const selectedContact = contacts.find(c => c.id === selectedContactId);

    return (
        <div className="relative w-full h-full bg-black">
            {/* MAP LAYER */}
            <TacticalMap
                userLocation={userLocation}
                userProfile={userProfile}
                contacts={contacts}
                hazards={hazards}
                news={globalNews}
                selectedId={selectedContactId}
                onSelectContact={handleSelectContact}
                onSelectHazard={onSelectHazard}
                onSelectNews={(id) => {
                    const newsItem = globalNews.find(n => n.id === id);
                    if (newsItem) onSelectNews(newsItem);
                }}
                onMapClick={() => setSelectedContactId(null)}
                flyToLocation={flyToLocation}
                onOpenChat={onOpenChat}
                onViewProfile={onViewProfile}
                onSendPulse={onSendPulse}
                onRegionChange={onRegionChange}
                mapStyle={mapStyle}
                onAutoStyleChange={setMapStyle}
                onSelectSelf={() => {
                    if (userProfile && userLocation) {
                        setFlyToLocation(userLocation);
                    }
                }}
                onContactHold={(id) => handleSelectContact(id)}
            />

            {/* OVERLAY CONTROLS */}
            <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-4 pointer-events-none">
                <button
                    onClick={onOpenMenu}
                    className="pointer-events-auto w-12 h-12 rounded-xl bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-[var(--neon-cyan)] hover:text-black transition-all"
                >
                    <Menu size={24} />
                </button>
            </div>

            <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-4 pointer-events-none">
                <button
                    onClick={onSos}
                    className="pointer-events-auto w-16 h-16 rounded-full bg-red-600/90 backdrop-blur-md border-4 border-red-500/30 flex items-center justify-center text-white animate-pulse shadow-[0_0_30px_rgba(239,68,68,0.4)] hover:scale-105 transition-transform"
                >
                    <Radio size={28} />
                </button>
            </div>

            <div className="absolute bottom-24 right-4 z-[1000] flex flex-col gap-2 pointer-events-none">
                <button
                    onClick={() => {
                        if (userLocation) setFlyToLocation(userLocation);
                    }}
                    className="pointer-events-auto w-12 h-12 rounded-full bg-black/60 backdrop-blur-md border border-white/10 flex items-center justify-center text-cyan-400 hover:bg-cyan-500/20 transition-all"
                >
                    <Crosshair size={24} />
                </button>
            </div>

            {/* TACTICAL HUD FOR SELECTED CONTACT */}
            {selectedContact && (
                <TacticalHUD
                    mode="CONTACT"
                    target={selectedContact}
                    onClose={() => setSelectedContactId(null)}
                    onAction={handleHudAction}
                />
            )}
        </div>
    );
};

export default CommandCenter;
